import { Component, inject, signal } from '@angular/core';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { ISchools } from '../../../../../interfaces/school';
import { IPayment, IPayments } from '../../../../../interfaces/fees';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { FeeService } from '../../../../../services/fees';
import { Helpers } from '../../../../../utilities/helpers';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { DecimalPipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';

@Component({
  selector: 'app-payments',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    ReactiveFormsModule,
    NgClass,
    FormsModule,
    DecimalPipe,
  ],
  templateUrl: './payments.html',
  styleUrl: './payments.css',
})
export class Payments {
  constructor(
    private session: Session,
    private schoolService: SchoolService,
    private feeService: FeeService,
    private router: Router,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getPayments();
  }

  protected schoolId?: string;
  protected readonly flashService = inject(FlashService);
  protected loggedUserRole?: string;
  protected schools = signal<ISchools | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected payments = signal<IPayments | null>(null);
  protected filterSchoolId: string = '';
  protected searchPayments: string = '';
  protected paymentStatus: string = '';
  protected readonly Helpers = Helpers;
  protected allowedStatuses: string[] = ['PENDING', 'COMPLETED', 'FAILED', 'REVERSED', 'CANCELLED'];
  protected setReversePayment = signal<boolean>(false);
  protected isEditing = signal<boolean>(false);
  protected reversalReason: string = '';
  protected setPaymentDetail = signal(false);
  protected paymentPercent: number = 1;
  protected studentFeePayments = signal<IPayments | null>(null);
  protected paymentDate!: string;

  protected reasonsOfReversal = [
    { label: 'Duplicate payment', value: 'DUPLICATE_PAYMENT' },
    { label: 'Incorrect amount entered', value: 'INCORRECT_AMOUNT' },
    { label: 'Applied to wrong student', value: 'WRONG_STUDENT' },
    { label: 'Bank / mobile transfer failed', value: 'TRANSFER_FAILED' },
    { label: 'Cheque bounced', value: 'CHEQUE_BOUNCED' },
    { label: 'Parent / guardian request', value: 'PARENT_REQUEST' },
    { label: 'System or data entry error', value: 'SYSTEM_ERROR' },
    { label: 'Other', value: 'OTHER' },
  ];

  protected selectedPayment: IPayment['payload'] = {
    id: '',
    public_id: '',
    school_id: '',
    student_id: '',
    student_public_id: '',
    student_fee_id: '',
    amount: 1,
    first_name: '',
    last_name: '',
    student_fee_public_id: '',
    student_fee_final_amount: 1,
    student_fee_remaining_balance: 1,
    student_fee_amount_paid: 1,
    student_fee_status: '',
    fee_name: '',
    fee_type: '',
    payment_date: '',
    payment_method: '',
    transaction_reference: 'N/A',
    received_by: '',
    status: '',
    level_public_id: '',
    level_name: '',
    level_code: '',
    class_name: '',
    class_public_id: '',
    notes: '',
    reversal_reason: '',
    reversed_by: '',
    created_at: '',
    updated_at: '',
    received_by_first_name: '',
    received_by_last_name: '',
    reversed_by_first_name: '',
    reversed_by_last_name: '',
  };

  protected navigateToStudentDetail(id: string): void {
    void this.router.navigate(['/dashboard/academic-management/student-details'], {
      queryParams: {
        id: id,
      },
    });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected getPayments(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.feeService.getPayments(`${ApiEndpoints.url}/payment`, params).subscribe({
      next: (res) => {
        this.payments.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load payments. Refresh the page',
        );
      },
    });
  }

  protected getStudentPayments(studentFeeId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      student_fee_id: studentFeeId,
      status: 'COMPLETED',
    };

    this.feeService.getPayments(`${ApiEndpoints.url}/payment`, params).subscribe({
      next: (res) => {
        this.studentFeePayments.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load payments. Refresh the page',
        );
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getPayments();
  }

  protected selectPayment(payment: IPayment['payload']): void {
    this.getStudentPayments(payment.student_fee_id);

    this.selectedPayment = payment;
    this.paymentPercent =
      (this.selectedPayment.student_fee_amount_paid /
        this.selectedPayment.student_fee_final_amount) *
      100;
    this.setPaymentDetail.set(true);
  }

  protected reversePayment(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isEditing.set(true);

    const reverseForm = {
      reversal_reason: this.reversalReason,
    };

    this.feeService
      .edit(`${ApiEndpoints.url}/payment/${this.selectedPayment.id}/reverse`, reverseForm)
      .subscribe({
        next: () => {
          this.flashService.success('Payment reversed successfully');
          window.location.reload();
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not reverse payment. Try again');
        },
      });
  }
}
